<template>
    <BaseEdge :id="id" :style="edgeStyle" :path="path[0]" :marker-end="markerEnd" />

    <EdgeLabelRenderer>
        <div v-if="!lockFlowCreatorStatus && !isViewMode" @mouseover="handleMouseEnter" @mouseleave="handleMouseLeave" :style="{
            pointerEvents: 'all',
            opacity: showDeleteButton ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${path[1]}px,${path[2]}px)`,
        }" class="nodrag nopan">
            <button class="edgebutton" @click="handleRemoveEdge"><v-icon size="16">mdi-close</v-icon></button>
        </div>
    </EdgeLabelRenderer>
</template>

<script>
import { BaseEdge, EdgeLabelRenderer, getBezierPath, getSmoothStepPath, useVueFlow } from '@vue-flow/core'

export default {
    inheritAttrs: false,
    components: {
        BaseEdge,
        EdgeLabelRenderer
    },
    inject: ['lockFlowCreatorStatus'],
    props: {
        id: {
            type: String,
            required: true,
        },
        sourceX: {
            type: Number,
            required: true,
        },
        sourceY: {
            type: Number,
            required: true,
        },
        targetX: {
            type: Number,
            required: true,
        },
        targetY: {
            type: Number,
            required: true,
        },
        sourcePosition: {
            type: String,
            required: true,
        },
        targetPosition: {
            type: String,
            required: true,
        },
        markerEnd: {
            type: String,
            required: false,
        },
        style: {
            type: Object,
            required: false,
        },
        source: {
            type: String,
            required: false,
        },
        target: {
            type: String,
            required: false,
        },
        sourceHandleId: {
            type: String,
            required: false,
        },
        targetHandleId: {
            type: String,
            required: false,
        },
        data: {
            type: Object,
            required: false,
        }
    },
    setup(props) {
        const vueFlowInstance = useVueFlow();
        const { removeEdges } = vueFlowInstance;
        
        const handleRemoveEdge = () => {
            removeEdges(props.id);
        };
        
        return { 
            handleRemoveEdge, 
            vueFlowInstance
        };
    },
    data() {
        return {
            showDeleteButton: false,
            mouseOverButton: false,
        };
    },
    watch: {
        style: {
            handler(newStyle) {
                if (this.mouseOverButton) return;

                if (newStyle.strokeWidth == 1.6) {
                    this.showDeleteButton = true;
                } else {
                    setTimeout(() => {
                        if (!this.mouseOverButton) {
                            this.showDeleteButton = false;
                        }
                    }, 300);
                }
            },
            deep: true,
        },
        mouseOverButton(newValue) {
            if (newValue) {
                this.showDeleteButton = true;
            } else {
                setTimeout(() => {
                    if (!this.mouseOverButton) {
                        this.showDeleteButton = false;
                    }
                }, 300);
            }
        }
    },
    mounted() {
        // console.log(this.lockFlowCreatorStatus)  // จะได้ true/false
    },
    computed: {
        isViewMode() {
            return this.$route?.query?.mode === 'view';
        },
        isParallelOrMergeEdge() {
            try {
                const targetNode = this.vueFlowInstance.nodes.value.find(node => node.id === this.target);
                const isTargetMerge = targetNode && targetNode.data?.type === 'merge';
                let isParallelSource = this.data?.isParallel === true;
                
                const sourceNode = this.vueFlowInstance.nodes.value.find(node => node.id === this.source);

                // Double check for Intent/SmartIntent nodes to prevent incorrect dashed lines
                if (isParallelSource) {
                    if (sourceNode && (sourceNode.data?.type === 'intent' || sourceNode.data?.type === 'smartIntent')) {
                        // Check if this handle has multiple connections
                        // Intent nodes usually have discrete outputs, so 1 connection per handle shouldn't be dashed
                        const edgesFromHandle = this.vueFlowInstance.edges.value.filter(e => 
                           e.source === this.source && e.sourceHandle === this.sourceHandleId
                        );
                        if (edgesFromHandle && edgesFromHandle.length < 2) {
                            isParallelSource = false;
                        }
                    }
                } else {
                    // Force check for fallback of intent/smartIntent even if parent isParallel is false/undefined
                    // This handles cases where Flow.vue parallel logic missed counting some connections (like Intent AI)
                    if (sourceNode && (sourceNode.data?.type === 'intent' || sourceNode.data?.type === 'smartIntent')) {
                        const edgesFromHandle = this.vueFlowInstance.edges.value.filter(e => 
                           e.source === this.source && e.sourceHandle === this.sourceHandleId
                        );
                        if (edgesFromHandle && edgesFromHandle.length >= 2) {
                            isParallelSource = true;
                        }
                    }
                }
                
                // Force parallel mode off if the connection is from local handles that shouldn't be parallel
                // e.g. Model Node bottom output (output-bottom)
                if (sourceNode && sourceNode.data?.type === 'model') {
                    if (this.sourceHandleId === 'output-bottom') {
                        isParallelSource = false;
                    }
                }
                
                return isParallelSource || isTargetMerge;
            } catch (error) {
                return false;
            }
        },
        edgeStyle() {
            const baseStyle = {
                strokeWidth: 1.5,
                ...this.style
            };
            
            // ถ้าเป็น parallel หรือ merge edge ให้ใช้เส้นประ (dashed)
            if (this.isParallelOrMergeEdge) {
                return {
                    ...baseStyle,
                    strokeDasharray: '8,5'  // ใช้ '2,2' สำหรับจุด (dotted)
                };
            }
            
            return baseStyle;
        },
        path() {
            // Knowledge nodes ที่ต้องใช้ Bezier curve แทน SmoothStep
            const knowledgeNodeTypes = ['documents', 'databaseIntegration', 'products', 'intentAI', 'apiknowledge', 'sharepoint'];
            
            try {
                // ตรวจสอบว่า target node เป็น Knowledge node หรือไม่
                const targetNode = this.vueFlowInstance.nodes.value.find(node => node.id === this.target);
                const isTargetKnowledgeNode = targetNode && knowledgeNodeTypes.includes(targetNode.data?.type);
                
                // ตรวจสอบว่า source node เป็น Knowledge node หรือไม่
                const sourceNode = this.vueFlowInstance.nodes.value.find(node => node.id === this.source);
                const isSourceKnowledgeNode = sourceNode && knowledgeNodeTypes.includes(sourceNode.data?.type);
                
                // ถ้า source หรือ target เป็น Knowledge node ให้ใช้ Bezier curve
                if (isTargetKnowledgeNode || isSourceKnowledgeNode) {
                    return getBezierPath(this);
                }
                
                // ตรวจสอบว่าเป็น parallel connection หรือไม่
                // 1. Parent AI Model (isParallel=true) -> child AI Model/Merge
                // 2. Any node -> Merge node
                // const isSourceModel = sourceNode && sourceNode.data?.type === 'model';
                // const isTargetModel = targetNode && targetNode.data?.type === 'model';
                // const isTargetMerge = targetNode && targetNode.data?.type === 'merge';
                // const isParallelSource = this.data?.isParallel === true;
                
                // ถ้าเป็น parallel connection หรือไปหา Merge ให้ใช้ SmoothStep (เส้นประ)
                // if (isParallelSource || isTargetMerge) {
                //     return getSmoothStepPath(this);
                // }
            } catch (error) {
                console.log("Error accessing nodes:", error);
                // ถ้า error ให้ fallback ไปใช้ logic เดิม
            }
            
            // Logic เดิม: ถ้า targetX < sourceX ใช้ SmoothStep, ไม่งั้นใช้ Bezier
            if (this.targetX < this.sourceX) {
                return getSmoothStepPath(this);
            }
            return getBezierPath(this);
        }
    },
    methods: {
        handleMouseEnter() {
            this.mouseOverButton = true;
        },
        handleMouseLeave() {
            this.mouseOverButton = false;
        }
    }
}
</script>

<style scoped>
.edge-button-container {
    opacity: 1;
    transition: opacity 0.3s ease-in-out;
    z-index: 10;
    /* Added z-index to bring the button on top */
}

.edgebutton {
    width: 24px;
    height: 24px;
    background-color: #ff6b6b;
    border: 2px solid #ffffff;
    border-radius: 50%;
    color: white;
    font-size: 14px;
    font-weight: bold;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    transition: all 0.2s ease-in-out;
    line-height: 1;
    padding: 0;
    opacity: 1;
}

.edgebutton:hover {
    background-color: #ff5252;
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}

.edgebutton:active {
    transform: scale(0.95);
}
</style>